import jsPDF from "jspdf";
import "jspdf-autotable";

import {
  dineroRemito,
  fechaRemitoVista,
  nombreEntidadRemito,
  estaAnuladaRemito,
  resumenOrdenRemito,
  numeroRemitoAplicado,
  nombreMedioRemito,
  referenciaMedioRemito,
} from "./ordenesRemitoVista";

export function generarPDFOrdenRemito(orden, tipo) {
  if (!orden?._id) {
    throw new Error("No se recibió una orden válida para generar el PDF.");
  }

  if (!["OC", "OP"].includes(tipo)) {
    throw new Error("Tipo de orden inválido.");
  }

  const esOP = tipo === "OP";
  const titulo = esOP ? "ORDEN DE PAGO" : "ORDEN DE COBRO";
  const persona = esOP ? orden.proveedor : orden.cliente;
  const resumen = resumenOrdenRemito(orden, tipo);
  const anulada = estaAnuladaRemito(orden);

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const ancho = doc.internal.pageSize.getWidth();
  const alto = doc.internal.pageSize.getHeight();
  const paginasConCabecera = new Set();

  function cabecera() {
    const pagina = doc.internal.getCurrentPageInfo().pageNumber;

    if (paginasConCabecera.has(pagina)) return;
    paginasConCabecera.add(pagina);

    doc.setFillColor(237, 244, 255);
    doc.rect(14, 10, ancho - 28, 22, "F");

    doc.setTextColor(23, 72, 139);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("DANLU · PAPAS", 18, 19);

    doc.setFontSize(10);
    doc.text(titulo, 18, 26);

    doc.setFontSize(9);
    doc.text(String(orden.numero || "-"), ancho - 18, 19, {
      align: "right",
    });

    doc.setTextColor(anulada ? 190 : 60, anulada ? 35 : 90, anulada ? 35 : 60);
    doc.text(
      anulada ? "ANULADA" : "VIGENTE",
      ancho - 18,
      26,
      { align: "right" }
    );

    doc.setTextColor(35, 45, 60);
    doc.setFont("helvetica", "normal");
  }

  cabecera();

  let y = 40;

  doc.setFontSize(9);
  doc.text(`Fecha: ${fechaRemitoVista(orden.fecha)}`, 14, y);
  y += 6;

  const textoPersona = doc.splitTextToSize(
    `${esOP ? "Proveedor" : "Cliente"}: ${nombreEntidadRemito(persona)}`,
    ancho - 28
  );

  doc.text(textoPersona, 14, y);
  y += textoPersona.length * 4.5 + 5;

  if (anulada) {
    doc.setTextColor(190, 35, 35);
    doc.text(
      "Orden anulada. Los importes se muestran como registro histórico.",
      14,
      y
    );
    doc.setTextColor(35, 45, 60);
    y += 8;
  }

  function tabla(tituloSeccion, encabezados, filas, opciones = {}) {
    const startY = y;

    doc.autoTable({
      startY,
      margin: { left: 14, right: 14, top: 42, bottom: 18 },
      head: [encabezados],
      body: filas,
      theme: "striped",
      styles: {
        font: "helvetica",
        fontSize: 8,
        cellPadding: 2.5,
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        fillColor: [31, 95, 209],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [245, 248, 252],
      },
      ...opciones,
      willDrawPage: () => {
        cabecera();

        const pagina = doc.internal.getCurrentPageInfo().pageNumber;
        const esPrimera = pagina === doc.internal.getNumberOfPages();

        // El título de sección se dibuja únicamente al comenzar la tabla.
        if (esPrimera && doc.lastAutoTable?.finalY === undefined) {
          // La cabecera de tabla identifica las columnas.
        }
      },
    });

    y = doc.lastAutoTable.finalY + 9;
  }

  function espacioParaTitulo(texto) {
    if (y > alto - 38) {
      doc.addPage();
      cabecera();
      y = 42;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(texto, 14, y);
    doc.setFont("helvetica", "normal");
    y += 4;
  }

  espacioParaTitulo("Aplicaciones a remitos");

  const aplicaciones = orden.aplicaciones || [];

  const encabezados = [
    "Remito",
    "Aplicado A",
    "Aplicado B",
    ...(esOP ? ["Flete"] : []),
    "Total aplicado",
  ];

  const filas = aplicaciones.map((a) => {
    const total =
      Number(esOP ? a.aplicado : a.importe) ||
      Number(a.aplicadoA || 0) +
        Number(a.aplicadoB || 0) +
        (esOP ? Number(a.aplicadoFlete || 0) : 0);

    return [
      String(numeroRemitoAplicado(a)),
      dineroRemito(a.aplicadoA),
      dineroRemito(a.aplicadoB),
      ...(esOP ? [dineroRemito(a.aplicadoFlete)] : []),
      dineroRemito(total),
    ];
  });

  const columnasNumericas = {};

  for (let i = 1; i < encabezados.length; i += 1) {
    columnasNumericas[i] = { halign: "right" };
  }

  tabla(
    "Aplicaciones",
    encabezados,
    filas.length
      ? filas
      : [
          [
            {
              content: "Sin aplicaciones a remitos · Anticipo",
              colSpan: encabezados.length,
            },
          ],
        ],
    { columnStyles: columnasNumericas }
  );

  const medios = esOP ? orden.pagos || [] : orden.medios || [];

  if (medios.length > 0) {
    espacioParaTitulo(esOP ? "Medios de pago" : "Medios recibidos");

    tabla(
      "Medios",
      ["Medio", "Referencia / valor", "Importe"],
      medios.map((m) => [
        `${nombreMedioRemito(m)}${m.carteraItem ? " · Cartera" : ""}`,
        referenciaMedioRemito(m),
        dineroRemito(m.importe),
      ]),
      {
        columnStyles: {
          0: { cellWidth: 38 },
          2: { cellWidth: 40, halign: "right" },
        },
      }
    );
  }

  const retenciones = orden.retenciones || [];

  if (retenciones.length > 0) {
    espacioParaTitulo("Retenciones");

    tabla(
      "Retenciones",
      ["Tipo", "Comprobante", "Importe"],
      retenciones.map((r) => [
        String(r.tipo || "-").toUpperCase(),
        String(r.comprobante || r.numero || "-"),
        dineroRemito(r.importe),
      ]),
      { columnStyles: { 2: { halign: "right" } } }
    );
  }

  const anticipos = orden.saldosFavorAplicados || [];

  if (anticipos.length > 0) {
    espacioParaTitulo("Anticipos utilizados");

    tabla(
      "Anticipos",
      ["Orden de origen", "Importe utilizado"],
      anticipos.map((a) => {
        const origen = esOP ? a.ordenPagoOrigen : a.ordenCobroOrigen;

        return [
          String(
            a.numeroOrigen ||
              origen?.numero ||
              origen?._id ||
              origen ||
              "-"
          ),
          dineroRemito(a.importe),
        ];
      }),
      { columnStyles: { 1: { halign: "right" } } }
    );
  }

  espacioParaTitulo("Resumen");

  tabla(
    "Resumen",
    ["Concepto", "Importe"],
    [
      ["Aplicado A", dineroRemito(resumen.aplicadoA)],
      ["Aplicado B", dineroRemito(resumen.aplicadoB)],
      ...(esOP
        ? [["Aplicado a flete", dineroRemito(resumen.aplicadoFlete)]]
        : []),
      ["Total aplicado a remitos", dineroRemito(resumen.aplicado)],
      [
        esOP ? "Total medios de pago" : "Total medios recibidos",
        dineroRemito(resumen.medios),
      ],
      ["Retenciones", dineroRemito(resumen.retenciones)],
      ["Anticipos utilizados", dineroRemito(resumen.anticipos)],
      ["Saldo a favor generado", dineroRemito(resumen.generado)],
    ],
    { columnStyles: { 1: { halign: "right" } } }
  );

  if (orden.observaciones) {
    espacioParaTitulo("Observaciones");

    tabla(
      "Observaciones",
      ["Detalle"],
      [[String(orden.observaciones)]]
    );
  }

  const cantidadPaginas = doc.internal.getNumberOfPages();

  for (let pagina = 1; pagina <= cantidadPaginas; pagina += 1) {
    doc.setPage(pagina);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(110, 120, 135);

    doc.text("DANLU · Comercialización de papas", 14, alto - 8);
    doc.text(
      `${pagina} / ${cantidadPaginas}`,
      ancho - 14,
      alto - 8,
      { align: "right" }
    );
  }

  const nombreArchivo = String(orden.numero || orden._id).replace(
    /[^a-zA-Z0-9_-]/g,
    "_"
  );

  doc.save(`${tipo}_Papas_${nombreArchivo}.pdf`);
}